/* global mixpanel */
import { put, call, select, all } from "redux-saga/effects"
import * as actions from "../action_creators"
import * as client from "../../api/client"
import {
    getProfile,
    getTeamDashboardId,
    getTeam,
    getSelectedTeamMembership,
    getSelectedTeamInvites,
    getPersonalTeamId,
} from "../selectors"
import { makeToast } from "../../util/toast_util"
import { toTitleCase } from "../../util/name_utils"
import {
    mappedEmails,
    searchEmailsForCurrentEmail,
} from "../../util/team_utils"
import { handleGetProfile } from "./profile_sagas"
import { handleBootstrap } from "./bootstrap_sagas"
import { alerts } from "../../util/alert_error_utils"
import { handleSignout } from "./session_saga"
import { prepareOnboardingTeamInvites } from "../../util/ui_saga_utils"
import GA from "react-ga"

const SHOUTBASE_GENERAL_PROBLEM =
    "Sorry, something went wrong. Please try again."

export function* getTeamMembers({ teamId }) {
    // get members
    try {
        const resp = yield call(client.getMembers, teamId)
        yield put(actions.receiveTeamMembers(teamId, resp))
    } catch (e) {
        const errorMsg = "Failed to fetch team members"
        console.log(errorMsg + ", error: ", e) // eslint-disable-line no-console
        yield call(makeToast, errorMsg, "danger")
        return
    }

    // get invites
    try {
        const invitesResp = yield call(client.getInvites, { teamID: teamId })
        if (invitesResp === undefined) return
        yield put(actions.receiveTeamInvites(teamId, invitesResp))
    } catch (e) {
        const errorMsg = "Failed to fetch team invites"
        console.log(errorMsg + ", error: ", e) // eslint-disable-line no-console
        yield call(makeToast, errorMsg, "danger")
        return
    }
}

export function* deleteInvite({ inviteId: id }) {
    try {
        const resp = yield call(client.deleteInvite, { id })
        if (resp === undefined) return

        const teamId = yield select(getTeamDashboardId)
        yield call(getTeamMembers, { teamId: teamId })

        mixpanel.track("Uninvite member", { teamId, inviteId: id })
        GA.event({category: "User", action: "Uninvite member"})

        // toast
        const msg = "Invite removed."
        const ind = "success"
        yield call(makeToast, msg, ind)
    } catch (e) {
        const errorMsg = "Failed to delete team invite"
        console.log(errorMsg + ", error: ", e) // eslint-disable-line no-console
        yield call(makeToast, errorMsg, "danger")
    }
}

export function* handleAcceptTeamInvite({ inviteId, history }) {
    try {
        // accept invite and bootstrap
        yield call(client.acceptInvite, inviteId)
        yield call(handleBootstrap)
        // get data from invite
        yield call(handleCurrentInvite, inviteId, history)
    } catch (e) {
        yield call(handleInviteErrors, history)
        return
    }
    // navigate to dashboard if onboarding is complete
    const { onboardingStep } = yield select(getProfile)

    if (onboardingStep === "done") {
        yield call(history.push, "/dashboard")
    }
}

export function* handleCurrentInvite(inviteId, history) {
    try {
        // get data from invite
        const getInviteResp = yield call(client.getInvite, inviteId)
        const resp = yield call(client.getMembers, getInviteResp.teamId)
        const admin = resp.find(item => item.userId === getInviteResp.inviterId)
        const team = yield select(getTeam, getInviteResp.teamId)
        // set current invite
        yield put(
            actions.setCurrentInvite({
                ...getInviteResp,
                teamName: team.name,
                inviterFullName: admin.fullName,
                inviterEmail: admin.primaryEmail,
            }),
        )
    } catch (e) {
        yield call(handleInviteErrors, history)
    }
}

function* handleInviteErrors(history) {
    // toast
    const { msg } = alerts["inviteError"]
    const ind = "danger"
    yield call(makeToast, msg, ind)
    // signout
    yield call(handleSignout)
    yield call(history.push, "/signIn")
}

export function* handleDisableMember({ member }) {
    try {
        yield call(client.disableMember, member)

        // create success toast
        const selectedTeamMembership = yield select(getSelectedTeamMembership)
        const {
            items: {
                [member.userId]: { primaryEmail },
            },
        } = selectedTeamMembership
        const fakeName = toTitleCase(primaryEmail.split("@")[0])
        const msg = `${fakeName} is now archived for this team.`
        const ind = "success"
        yield call(makeToast, msg, ind)
        
        // update team
        const { teamId, userId } = member
        yield call(getTeamMembers, { teamId })
        // update profile with membership removed
        yield call(handleGetProfile)
        mixpanel.track("Disable member", { teamId, userId })
        GA.event({category: "User", action: "Disable member"})
    } catch (e) {
        // create failure toast
        const msg = SHOUTBASE_GENERAL_PROBLEM
        const ind = "warning"
        yield call(makeToast, msg, ind)
    }
}

export function* handleUpdateMemberRole({ teamMember, role }) {
    try {
        const updateMeta = {
            ...teamMember,
            accessLevel: role,
        }

        const resp = yield call(client.putMember, updateMeta)
        if (resp && resp.teamId) {
            yield call(getTeamMembers, { teamId: resp.teamId })
            const msg = `Access level has been updated. ${teamMember.fullName ||
                "This team member"}'s access level is now: ${role}.`
            const ind = "success"
            yield call(makeToast, msg, ind)
        }
    } catch (e) {
        const msg =
            "Something went wrong updating access level. Please try again."
        const ind = "warning"
        yield call(makeToast, msg, ind)
    }
}

export function* handleAddMember({ member }) {
    const team = yield select(getTeam, member.teamId)
    if (!team) {
        // if the team is not loaded, refetch the user profile
        // to try and catch up the loaded teams
        yield put(actions.getProfile())
    } else {
        // team is loaded in redux; is it a member of the currently selected team?
        const teamId = yield select(getTeamDashboardId)
        if (teamId === member.teamId) {
            // it is ... reload that team's roster
            yield call(getTeamMembers, { teamId: teamId })
        }
    }
    // toast
    const fakeName = toTitleCase(member.primaryEmail.split("@")[0])
    const msg = `${fakeName} has been added.`
    const ind = "success"
    yield call(makeToast, msg, ind)
}

export function* createInvite({ invite }) {
    // get current user's email address

    const {
        email: { address },
    } = yield select(getProfile)
    const selectedTeamMembers = yield select(getSelectedTeamMembership)
    const selectedTeamInvites = yield select(getSelectedTeamInvites)

    // get emails from all outstanding invite emails
    const mappedTeamInviteEmails = mappedEmails(
        selectedTeamInvites,
        "invitedEmail",
    )

    // if isActiveExistingMember returns an id, this email has already been invited or this email is already associated with a team member
    const isActiveExistingMember = selectedTeamMembers.sort.find(
        teamMemberId => {
            const teamMember = selectedTeamMembers.items[teamMemberId]
            return (
                teamMember.primaryEmail === invite.invitedEmail && teamMember.isActive === true
            )
        },
    )

    const searchInviteEmailsForCurrentEmail = searchEmailsForCurrentEmail(
        mappedTeamInviteEmails,
        invite.invitedEmail,
    )

    if (invite.invitedEmail === address) {
        // toast
        const msg = "You can't invite yourself."
        const ind = "danger"
        yield call(makeToast, msg, ind)
        return
    }

    if (isActiveExistingMember) {
        // toast
        const msg = "Member has already been added."
        const ind = "danger"
        yield call(makeToast, msg, ind)
        return
    }

    if (searchInviteEmailsForCurrentEmail) {
        // toast
        const msg = "Invite has already been sent."
        const ind = "danger"
        yield call(makeToast, msg, ind)
        return
    }

    try {
        const resp = yield call(client.postInvite, invite)
        if (resp === undefined) return

        const { teamId, invitedId, invitedEmail, inviterId } = invite
        yield call(getTeamMembers, { teamId })

        mixpanel.track("Invite member", {
            teamId,
            invitedId,
            invitedEmail,
            inviterId,
        })
        GA.event({category: "User", action: "Invite member"})
    } catch (e) {
        const errorMsg = "Failed to create invite"
        console.log(errorMsg + ", error: ", e) // eslint-disable-line no-console
        yield call(makeToast, errorMsg, "danger")
    }
}

export function* createTeam({ team, history }) {
    const { onboardingStep } = yield select(getProfile)
    const personalTeamId = yield select(getPersonalTeamId)
    const { accountID } = yield select(getTeam, personalTeamId)

    // update team with user's account id
    const updatedTeam = { ...team, accountID }

    let resp
    try {
        resp = yield call(client.postTeam, updatedTeam)
        if (resp === undefined) return
    } catch (e) {
        const errorMsg = "Failed to create team"
        console.log(errorMsg + ", error: ", e) // eslint-disable-line no-console
        yield call(makeToast, errorMsg, "danger")
        return
    }

    // fetch updated profile with team memberships
    yield call(handleGetProfile)

    // only toast or reroute if onboarding is complete
    if (onboardingStep === "done") {
        // toast
        const msg = `Team ${team.name} created.`
        const ind = "success"
        yield call(makeToast, msg, ind)

        yield call(history.push, "/teams/" + resp.id)
    }

    return resp.id
}

export function* updateTeam({ team }) {
    const { accountID } = yield select(getTeam, team.id)
    try {
        const resp = yield call(client.putTeam, {...team, accountID})
        if (resp === undefined) return

        yield put(actions.receiveTeam(resp))
        // toast
        const msg = `Team ${team.name} updated.`
        const ind = "success"
        yield call(makeToast, msg, ind)
    } catch (e) {
        const errorMsg = "Failed to update team"
        console.log(errorMsg + ", error: ", e) // eslint-disable-line no-console
        yield call(makeToast, errorMsg, "danger")
    }
}

export function* handleSendBulkInvites({ teamId, fields }) {
    yield call(sendBulkInvites, teamId, fields)
}

export function* sendBulkInvites(teamId, fields) {
    const invites = prepareOnboardingTeamInvites(fields, teamId)
    const emails = Object.keys(invites)
    if (!emails.length) return

    yield all(
        emails.map(email => {
            const invite = invites[email]
            return call(createInvite, { invite })
        }),
    )
}



// WEBPACK FOOTER //
// ./src/redux/sagas/team_sagas.js